/**
 * UserMenu — avatar + dropdown for the authenticated user.
 * Entries: profile (read-only), Settings, Logout.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  ListItemIcon,
  Divider,
  Typography,
  Box,
} from '@mui/material';
import { LogOut as LogOutIcon, Settings as SettingsIcon } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';

export const UserMenu: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleLogout = async () => {
    handleClose();
    try {
      await logout();
    } finally {
      navigate('/login', { replace: true });
    }
  };

  const initial = (user?.username?.[0] ?? 'U').toUpperCase();

  return (
    <>
      <IconButton onClick={handleOpen} size="small" sx={{ ml: 1 }} aria-label="Open user menu">
        <Avatar
          sx={{
            width: 30,
            height: 30,
            bgcolor: 'action.hover',
            color: 'primary.main',
            fontSize: '0.85rem',
            border: '1px solid',
            borderColor: 'primary.main',
          }}
        >
          {initial}
        </Avatar>
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2">{user?.username ?? 'User'}</Typography>
          <Typography variant="caption" color="text.secondary">
            {user?.role ?? ''}
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={() => { handleClose(); navigate('/settings'); }}>
          <ListItemIcon><SettingsIcon size={16} /></ListItemIcon>
          Settings
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon><LogOutIcon size={16} /></ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
    </>
  );
};

export default UserMenu;
